import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Alert,
	IconButton,
	Collapse,
	FormControl,
	FormLabel,
	TextField,
	Stack,
	Select,
	MenuItem,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { Error } from "@mui/icons-material";
import React from "react";
import { CreateClass } from "../FirebaseQueries";
import { Form, useNavigate } from "react-router-dom";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { Timestamp } from "firebase/firestore";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import NumberSpinner from "./NumberSpinner";
import { PickerValue } from "@mui/x-date-pickers/internals";
import { scenarioTitles } from "../Scenarios/Scenarios";
import { useAuth } from '../AuthProvider';


interface Props {
	isOpen: boolean;
	onClose: () => void;
}


export default function CreateClassDialog(props: Props) {
	const { isOpen, onClose } = props;
	const { currentUser } = useAuth();

	const [errorCreatingClass, setErrorCreatingClass] = React.useState(false);

	const [code, setCode] = React.useState("");
	const [session, setSession] = React.useState<number>(1);
	const [scenarioIndex, setScenarioIndex] = React.useState<number>(0);
	const [startTime, setStartTime] = React.useState<Date>(dayjs().second(0).toDate());
	const [endTime, setEndTime] = React.useState<Date>(dayjs().second(0).add(1, "h").toDate());

	const [codeErrorText, setCodeErrorText] = React.useState("");
	const [startTimeErrorText, setStartTimeErrorText] = React.useState("");
	const [endTimeErrorText, setEndTimeErrorText] = React.useState("");

	const navigate = useNavigate();

	const handleOnCreate = async () => {
		try {
			if (code === "") {
				setCodeErrorText("Class cannot be empty.");
			} else {
				const userId = currentUser !== null ? currentUser.uid : "";
				const classRef = await CreateClass(code, userId, session, scenarioIndex, Timestamp.fromDate(startTime), Timestamp.fromDate(endTime));
				navigate(`/app/classes/${classRef.id}`);
			}
		} catch (err) {
			setErrorCreatingClass(true);
			onClose();
			console.log(err);
		}
	}

	const handleClassUpdate = (event: any) => {
		setCode(event.target.value);
		setCodeErrorText("");
	}

	const handleSessionUpdate = (value: any) => {
		if (value !== null) setSession(value);
	}

	const handleScenarioSelect = (event: any) => {
		setScenarioIndex(event.target.value);
	}

	const handleStartTimeChanged = (value: PickerValue) => {
		console.log(value);
		if (value !== null) {
			setStartTime(value.second(0).toDate());
			setStartTimeErrorText("");
		} else {
			// Error
			setStartTimeErrorText("This is not a valid date.");
		}
	}

	const handleEndTimeChanged = (value: PickerValue) => {
		if (value !== null) {
			setEndTime(value.second(0).toDate());
			setEndTimeErrorText("");
		} else {
			// Error
			setEndTimeErrorText("This is not a valid date.");
		}
	}

	return (
		<>
			<Dialog onClose={onClose} open={isOpen}>
				<DialogTitle>Create a Class</DialogTitle>
				<DialogContent sx={{ minWidth: 400 }}>
					<Stack spacing={1}>
						<FormControl>
							<FormLabel htmlFor="class">Class</FormLabel>
							<TextField
								id="class"
								value={code}
								placeholder="Class"
								variant="outlined"
								onChange={handleClassUpdate}
								error={codeErrorText !== ""}
							>
							</TextField>
							<span style={{ color: "#c20a0a", minHeight: 22 }}>{codeErrorText}</span>
						</FormControl>
						<FormControl>
							<FormLabel htmlFor="session">Session</FormLabel>
							<NumberSpinner value={session} onValueChange={handleSessionUpdate} min={1} />
							<span style={{ minHeight: 22 }}></span>
						</FormControl>
						<FormControl sx={{ m: 1, minWidth: 160 }}>
							<FormLabel htmlFor="scenario-select">Scenario</FormLabel>
							<Select
								labelId="scenario-label"
								id="scenario-select"
								value={scenarioIndex}
								label="Scenario"
								onChange={handleScenarioSelect}
							>
								{scenarioTitles.map((item, index) => (
									<MenuItem key={index} value={index}>{item}</MenuItem>
								))}
							</Select>
							<span style={{ color: "#c20a0a", minHeight: 22 }}></span>
						</FormControl>
						<Stack direction="row" spacing={1}>
							<FormControl>
								<FormLabel htmlFor="start-time">Start Time</FormLabel>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DateTimePicker
										onChange={handleStartTimeChanged}
										value={dayjs(startTime)}
									/>
								</LocalizationProvider>
								<span style={{ color: "#c20a0a", minHeight: 22 }}>{startTimeErrorText}</span>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="start-time">End Time</FormLabel>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DateTimePicker
										onChange={handleEndTimeChanged}
										value={dayjs(endTime)}
									/>
								</LocalizationProvider>
								<span style={{ color: "#c20a0a", minHeight: 22 }}>{endTimeErrorText}</span>
							</FormControl>
						</Stack>
					</Stack>
					<br />
					<DialogActions>
						<Button onClick={onClose}>Cancel</Button>
						<Button onClick={handleOnCreate}>Create</Button>
					</DialogActions>
				</DialogContent>
			</Dialog>

			<Collapse in={errorCreatingClass} mountOnEnter unmountOnExit>
				<Alert icon={<Error fontSize="inherit" />} severity="error"
					action={
						<IconButton
							aria-label="close"
							color="inherit"
							size="small"
							onClick={() => {
								setErrorCreatingClass(false);
							}}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}>
					There was an error creating the class. Please try again.
				</Alert>
			</Collapse>
		</>
	)
}